import { useCallback, useEffect, useRef } from "react";
import type { GeolocationPosition, Position, TenantApp } from "@inloco/shared";
import type { RoomSession, RoomSessionForJoining, RoomSessionWithoutId } from "../../types/room-session";
import { useAuth } from "../context";

interface UseRoomOrchestrationProps {
    app: TenantApp | null;
    position: GeolocationPosition | null;
    roomSession: RoomSession | null;
    createNewRoom: (app: TenantApp, roomSession: RoomSessionWithoutId, position: Position) => Promise<void>;
    joinExistingRoom: (app: TenantApp, roomSession: RoomSessionForJoining, position: Position) => Promise<void>;
    removeUserFromRoom: (app: TenantApp, roomId: string, userId: string) => Promise<void>;
    saveBluetoothToRoom: (app: TenantApp, roomId: string, macAddress: string, name: string) => Promise<void>;
}

interface UseRoomOrchestrationReturn {
    // questo chiama createNewRoom che sta in useRoom
    handleRegistration: (roomSession: RoomSessionWithoutId) => Promise<void>;
    // questo chiama removeUserFromRoom che sta in useRoom
    handleLogout: () => Promise<void>;
    // questo chiama joinExistingRoom che sta in useRoom
    handleJoinScannedRoom: (sessionForJoining: RoomSessionForJoining) => Promise<void>;
    // questo chiama saveBluetoothToRoom che sta in useRoom
    handleAddBluetoothDevice: (macAddress: string, name: string) => Promise<void>;
}

export const useRoomOrchestration = (props: UseRoomOrchestrationProps): UseRoomOrchestrationReturn => {
    const {
        app,
        position,
        roomSession,
        createNewRoom,
        joinExistingRoom,
        removeUserFromRoom,
        saveBluetoothToRoom,
    } = props;

    const { userFirebase } = useAuth();

    // Ref to avoid circular dependencies
    const roomSessionRef = useRef(roomSession);

    // Update ref when roomSession changes
    useEffect(() => {
        roomSessionRef.current = roomSession;
    }, [roomSession]);

    const handleRegistration = useCallback(async (userSessionWithoutRoom: RoomSessionWithoutId) => {

        const { username } = userSessionWithoutRoom;

        if (!username || !userFirebase || !app || !position) {
            return;
        }

        await createNewRoom(app, userSessionWithoutRoom, position);

    }, [app, position, userFirebase, createNewRoom]);

    const handleLogout = useCallback(async () => {
        const session = roomSessionRef.current;
        const roomId = session?.roomId;

        // First remove from Firebase
        if (roomId && userFirebase && app) {
            const userId = userFirebase.uid
            await removeUserFromRoom(app, roomId, userId);
        }

    }, [app, userFirebase, removeUserFromRoom]);

    const handleJoinScannedRoom = useCallback(async (sessionForJoining: RoomSessionForJoining) => {

        const { roomId } = sessionForJoining;

        if (!position || !roomId || !userFirebase || !app) {
            return;
        }

        await joinExistingRoom(app, sessionForJoining, position);


    }, [app, userFirebase, position, joinExistingRoom]);

    const handleAddBluetoothDevice = useCallback(async (macAddress: string, name: string) => {
        const session = roomSessionRef.current;
        const roomId = session?.roomId;

        if (!roomId || !userFirebase || !app) {
            return;
        }

        await saveBluetoothToRoom(app, roomId, macAddress, name);

    }, [app, userFirebase, saveBluetoothToRoom]);

    return {
        handleRegistration,
        handleLogout,
        handleJoinScannedRoom,
        handleAddBluetoothDevice
    };
};
